package com.example.borshi.web.cart

import androidx.compose.runtime.mutableStateListOf
import com.example.borshi.web.model.Product
import kotlinx.browser.localStorage
import kotlin.js.Date
import kotlin.js.JSON

// Unified Cart Store handling both One-Time purchases and Subscriptions
class CartStore {
    companion object {
        private const val CART_STORAGE_KEY = "borshi_cart_v1"
    }

    // We hold all items in a single list of the sealed type
    private val _items = mutableStateListOf<CartLineItem>()
    val items: List<CartLineItem> get() = _items

    // Computed properties for UI convenience
    val oneTimeItems: List<CartLineItem.OneTime>
        get() = _items.filterIsInstance<CartLineItem.OneTime>()

    val subscriptionItems: List<CartLineItem.Subscription>
        get() = _items.filterIsInstance<CartLineItem.Subscription>()

    private var deliveryFeePolicyThreshold: Double = 500.0
    private var standardDeliveryFee: Double = 50.0

    // --- Persistence Methods ---

    /**
     * Save the current cart state to localStorage.
     * Uses simple string-based JSON construction.
     */
    private fun saveToLocalStorage() {
        console.log("🛒 CartStore: Saving ${_items.size} items to localStorage...")
        try {
            val jsonItems = _items.joinToString(separator = ",\n  ", prefix = "[\n  ", postfix = "\n]") { item ->
                when (item) {
                    is CartLineItem.OneTime -> buildString {
                        append("""{""")
                        append(""""type":"OneTime",""")
                        append(""""id":"${item.id}",""")
                        append(""""productId":"${item.productId}",""")
                        append(""""name":"${item.name.replace("\"", "\\\"")}",""")
                        append(""""price":${item.price},""")
                        append(""""imageUrl":${if (item.imageUrl != null) "\"${item.imageUrl}\"" else "null"},""")
                        append(""""weight":"${item.weight}",""")
                        append(""""quantity":${item.quantity}""")
                        append("""}""")
                    }
                    is CartLineItem.Subscription -> buildString {
                        append("""{""")
                        append(""""type":"Subscription",""")
                        append(""""id":"${item.id}",""")
                        append(""""productId":"${item.productId}",""")
                        append(""""name":"${item.name.replace("\"", "\\\"")}",""")
                        append(""""price":${item.price},""")
                        append(""""imageUrl":${if (item.imageUrl != null) "\"${item.imageUrl}\"" else "null"},""")
                        append(""""packSize":"${item.packSize}",""")
                        append(""""quantityPerDelivery":${item.quantityPerDelivery},""")
                        append(""""schedule":{""")
                        append(""""frequency":"${item.schedule.frequency.name}",""")
                        append(""""startDate":${item.schedule.startDate},""")
                        append(""""endDate":${item.schedule.endDate ?: "null"},""")
                        append(""""slot":"${item.schedule.slot.name}",""")
                        append(""""customDays":[${item.schedule.customDays.joinToString(",") { "\"${it.name}\"" }}]""")
                        append("""},""")
                        append(""""payRule":"${item.payRule.name}"""")
                        append("""}""")
                    }
                }
            }

            localStorage.setItem(CART_STORAGE_KEY, jsonItems)
            console.log("🛒 CartStore: ✅ Cart saved successfully to localStorage: ${_items.size} items")
        } catch (e: Exception) {
            console.error("🛒 CartStore: ❌ Failed to save cart to localStorage:", e)
        }
    }

    /**
     * Restore cart state from localStorage.
     * Uses native JavaScript JSON for maximum compatibility.
     */
    fun hydrateFromLocalStorage() {
        console.log("🛒 CartStore: Starting hydration from localStorage...")
        try {
            val stored = localStorage.getItem(CART_STORAGE_KEY)
            if (stored == null) {
                console.log("🛒 CartStore: No saved cart found in localStorage")
                return
            }

            console.log("🛒 CartStore: Found stored data, deserializing...")
            val jsArray = JSON.parse<Array<dynamic>>(stored)
            _items.clear()

            jsArray.forEach { jsObj ->
                val type = jsObj.type as? String ?: return@forEach

                when (type) {
                    "OneTime" -> {
                        _items.add(CartLineItem.OneTime(
                            id = jsObj.id as String,
                            productId = jsObj.productId as String,
                            name = jsObj.name as String,
                            price = (jsObj.price as Number).toDouble(),
                            imageUrl = jsObj.imageUrl as? String,
                            weight = jsObj.weight as? String ?: "500g",
                            quantity = (jsObj.quantity as? Number)?.toInt() ?: 1
                        ))
                    }
                    "Subscription" -> {
                        val scheduleObj = jsObj.schedule ?: return@forEach
                        val customDaysArray = scheduleObj.customDays as? Array<*> ?: emptyArray<Any>()

                        _items.add(CartLineItem.Subscription(
                            id = jsObj.id as String,
                            productId = jsObj.productId as String,
                            name = jsObj.name as String,
                            price = (jsObj.price as Number).toDouble(),
                            imageUrl = jsObj.imageUrl as? String,
                            packSize = jsObj.packSize as? String ?: "500ml",
                            quantityPerDelivery = (jsObj.quantityPerDelivery as? Number)?.toInt() ?: 1,
                            schedule = SubscriptionSchedule(
                                frequency = OrderFrequency.valueOf(scheduleObj.frequency as String),
                                startDate = (scheduleObj.startDate as Number).toLong(),
                                endDate = (scheduleObj.endDate as? Number)?.toLong(),
                                slot = DeliverySlot.valueOf(scheduleObj.slot as String),
                                customDays = customDaysArray.mapNotNull {
                                    try { Weekday.valueOf(it as String) } catch (e: Exception) { null }
                                }
                            ),
                            payRule = SubscriptionPayRule.valueOf(jsObj.payRule as? String ?: "PAY_FIRST_DELIVERY_TODAY")
                        ))
                    }
                }
            }

            console.log("🛒 CartStore: Cart hydrated successfully! ${_items.size} items restored")
        } catch (e: Exception) {
            console.error("🛒 CartStore: Failed to hydrate cart from localStorage:", e)
            // Clear corrupted data
            localStorage.removeItem(CART_STORAGE_KEY)
        }
    }

    /**
     * Clear persisted cart data from localStorage.
     */
    fun clearPersistedCart() {
        try {
            localStorage.removeItem(CART_STORAGE_KEY)
            console.log("Persisted cart cleared from localStorage")
        } catch (e: Exception) {
            console.error("Failed to clear persisted cart:", e)
        }
    }

    // --- Action: Add One-Time Product ---
    fun addProduct(product: Product, weight: String = "500g") {
        val uniqueId = "${product.id}_ot_${weight.replace(" ", "")}"
        val newItem = CartLineItem.OneTime(
            id = uniqueId,
            productId = product.id,
            name = product.name,
            price = product.price, // Assuming product.price is numeric
            imageUrl = product.imageUrl,
            weight = weight,
            quantity = 1
        )
        addOrUpdateOneTimeItem(newItem)
        saveToLocalStorage()
    }

    // Helper to merge quantities if exact item exists
    private fun addOrUpdateOneTimeItem(newItem: CartLineItem.OneTime) {
        val existingIndex = _items.indexOfFirst {
            it is CartLineItem.OneTime && it.id == newItem.id
        }

        if (existingIndex >= 0) {
            val existing = _items[existingIndex] as CartLineItem.OneTime
            _items[existingIndex] = existing.copy(quantity = existing.quantity + 1)
        } else {
            _items.add(newItem)
        }
    }

    // --- Action: Add Subscription ---
    fun addSubscription(
        product: Product,
        packSize: String,
        qtyPerDelivery: Int,
        schedule: SubscriptionSchedule,
        payRule: SubscriptionPayRule = SubscriptionPayRule.PAY_FIRST_DELIVERY_TODAY,
        mergeIfSame: Boolean = true
    ) {
        // Build a unique key for merging comparison (excluding dynamic ID)
        fun isSame(sub: CartLineItem.Subscription): Boolean {
            return sub.productId == product.id &&
                   sub.packSize == packSize &&
                   sub.schedule == schedule &&
                   sub.payRule == payRule
        }

        if (mergeIfSame) {
            val existingIndex = _items.indexOfFirst { it is CartLineItem.Subscription && isSame(it) }
            if (existingIndex >= 0) {
                val existing = _items[existingIndex] as CartLineItem.Subscription
                _items[existingIndex] = existing.copy(quantityPerDelivery = existing.quantityPerDelivery + qtyPerDelivery)
                saveToLocalStorage()
                return
            }
        }

        val subId = "${product.id}_sub_${Date().getTime()}" // Unique ID for every new sub setup
        val newSub = CartLineItem.Subscription(
            id = subId,
            productId = product.id,
            name = product.name,
            price = product.price,
            imageUrl = product.imageUrl,
            packSize = packSize,
            quantityPerDelivery = qtyPerDelivery,
            schedule = schedule,
            payRule = payRule
        )
        _items.add(newSub)
        saveToLocalStorage()
    }

    // --- Action: Update One-Time Item Quantity ---
    fun updateOneTimeQty(itemId: String, newQty: Int) {
        if (newQty <= 0) {
            removeItem(itemId)
            return
        }
        val index = _items.indexOfFirst { it.id == itemId && it is CartLineItem.OneTime }
        if (index >= 0) {
            val item = _items[index] as CartLineItem.OneTime
            _items[index] = item.copy(quantity = newQty)
            saveToLocalStorage()
        }
    }

    // --- Action: Update Subscription Quantity (Per Delivery) ---
    fun updateSubscriptionQty(itemId: String, newQtyPerDelivery: Int) {
        if (newQtyPerDelivery <= 0) return // Don't remove subs via qty 0, use explicit remove
        val index = _items.indexOfFirst { it.id == itemId && it is CartLineItem.Subscription }
        if (index >= 0) {
            val item = _items[index] as CartLineItem.Subscription
            _items[index] = item.copy(quantityPerDelivery = newQtyPerDelivery)
            saveToLocalStorage()
        }
    }

    // --- Action: Remove Item ---
    fun removeItem(id: String) {
        _items.removeAll { it.id == id }
        saveToLocalStorage()
    }

    fun clear() {
        _items.clear()
        saveToLocalStorage()
    }

    fun itemCount(): Int {
        return _items.sumOf {
            when (it) {
                is CartLineItem.OneTime -> it.quantity
                is CartLineItem.Subscription -> 1 // Count subscription as 1 "plan"
            }
        }
    }

    // --- Compute Totals ---
    fun computeTotals(): CartTotals {
        val oneTimeSubtotal = oneTimeItems.sumOf { it.price * it.quantity }

        // Subscription Logic based on PayRule
        val subFirstDeliveryTotal = subscriptionItems.sumOf { sub ->
            when (sub.payRule) {
                SubscriptionPayRule.PAY_PER_DELIVERY -> 0.0 // Pay nothing today for subscription
                SubscriptionPayRule.PAY_FIRST_DELIVERY_TODAY -> sub.price * sub.quantityPerDelivery
                SubscriptionPayRule.PREPAID_MONTHLY -> sub.price * sub.quantityPerDelivery * 30 // Example placeholder
            }
        }

        // Calculate gross total for delivery fee threshold (usually based on what you pay today OR total value)
        // Let's assume delivery fee threshold is based on Pay Today amount for simplicity, or we can include sub value.
        // Usually, if I buy a subscription, delivery might be free for the subscription part.
        // Let's stick to: Delivery fee applies if PayToday < Threshold.

        val payTodaySubtotal = oneTimeSubtotal + subFirstDeliveryTotal

        // Delivery Logic: waived if payToday items > threshold OR if there's a subscription (often subscriptions have free delivery)
        // For this requirement: "waive above threshold"
        val deliveryFee = if (payTodaySubtotal > 0 && payTodaySubtotal < deliveryFeePolicyThreshold) standardDeliveryFee else 0.0

        val tax = 0.0 // Placeholder

        val payToday = payTodaySubtotal + deliveryFee + tax

        return CartTotals(
            oneTimeSubtotal = oneTimeSubtotal,
            subscriptionFirstDeliverySubtotal = subFirstDeliveryTotal,
            deliveryFee = deliveryFee,
            taxAndPackaging = tax,
            payTodayTotal = payToday,
            itemCount = itemCount()
        )
    }
}

